import { useEffect, useState } from "react";
import { audio } from "../lib/audio";
import type { MailConfig, MailMessage } from "../scenario/types";
import { WindowTitlebar } from "./WindowTitlebar";

function initialsOf(m: MailMessage): string {
  if (m.initials) return m.initials;
  const parts = m.from.split(/\s+/).filter(Boolean);
  return (parts[0]?.[0] ?? "?") + (parts[1]?.[0] ?? "");
}

/**
 * Outlook-style mail client (crisp DOM). Inbox list + reading pane. Selecting an
 * email opens it; the scenario lure email drives the beat via onOpenEmail.
 */
const ATT_ICON: Record<string, string> = {
  exe: "\u2699",
  doc: "\u{1F4C4}",
  pdf: "\u{1F4D5}",
  zip: "\u{1F5DC}",
  xls: "\u{1F4D7}",
};

export function Mail({
  config,
  onOpenEmail,
  onClickLink,
  onSaveAttachment,
  saveAttachmentEmailId,
  continueEmailId,
  onContinue,
  replyEmailId,
  onReply,
}: {
  config: MailConfig;
  onOpenEmail: (id: string) => void;
  /** fired when the learner clicks the phishing link inside the email body. */
  onClickLink?: (id: string) => void;
  /** fired when the learner saves an attachment from the open email. */
  onSaveAttachment?: (emailId: string, attachmentId: string) => void;
  /** when set, the open email's attachment "Save" buttons drive the beat. */
  saveAttachmentEmailId?: string;
  /** if set, the reading pane shows a "Continue" button for this email so the
   *  learner can read the body before the beat advances (read_email beats). */
  continueEmailId?: string;
  onContinue?: (id: string) => void;
  /** when set, the open email shows a "Reply" affordance; sending it fires the
   *  reply (reply-only lures, where the mistake is simply answering). */
  replyEmailId?: string;
  onReply?: (id: string) => void;
}) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  useEffect(() => {
    const onGuide = (ev: Event) => {
      const label = String((ev as CustomEvent).detail || "").trim().toLowerCase();
      if (!label) return;
      if (label === "send reply") {
        const msg = config.emails.find((m) => m.replyDraft) ?? config.emails[0];
        if (!msg) return;
        setSelectedId(msg.id);
        onOpenEmail(msg.id);
        onReply?.(msg.id);
        return;
      }
      const msg = config.emails.find((m) => (m.cta?.label || "").trim().toLowerCase() === label);
      if (!msg) return;
      setSelectedId(msg.id);
      onOpenEmail(msg.id);
      onClickLink?.(msg.id);
    };
    window.addEventListener("cf-guide-action", onGuide);
    return () => window.removeEventListener("cf-guide-action", onGuide);
  }, [config, onClickLink, onOpenEmail, onReply]);
  const [read, setRead] = useState<Set<string>>(new Set());
  const [hoverHref, setHoverHref] = useState<string | null>(null);
  const [replied, setReplied] = useState<string | null>(null);
  const selected = config.emails.find((m) => m.id === selectedId) ?? null;

  const open = (m: MailMessage) => {
    if (m.id !== selectedId) audio.ui("pageOpen");
    setSelectedId(m.id);
    setRead((r) => new Set(r).add(m.id));
    onOpenEmail(m.id);
  };

  return (
    <div className="winapp mail">
      <WindowTitlebar title={config.appTitle ?? "Outlook"} />
      <div className="mail-toolbar">
        <button className="mail-new" type="button">{"\u2709"} New mail</button>
        <span className="mail-tool">{"\u{1F5D1}"} Delete</span>
        <span className="mail-tool">{"\u{1F4E6}"} Archive</span>
        <span className="mail-tool">{"\u26A0"} Report</span>
      </div>

      <div className="mail-body">
        <div className="mail-list" role="list">
          {config.emails.map((m) => {
            const unread = m.unread && !read.has(m.id);
            // Blink the lure email until it's been opened, to show where to click.
            const blink = m.lure && !read.has(m.id) && selectedId !== m.id;
            const done = m.badge === "Done";
            return (
              <button
                key={m.id}
                role="listitem"
                type="button"
                className={`mail-item ${selectedId === m.id ? "active" : ""} ${unread ? "unread" : ""} ${blink ? "blink" : ""} ${done ? "is-done" : ""}`}
                onClick={() => open(m)}
              >
                <span className="mail-avatar">{initialsOf(m)}</span>
                <span className="mail-item-text">
                  <span className="mail-from">{m.from}</span>
                  <span className="mail-subject">{m.subject}</span>
                  <span className="mail-preview">{m.preview}</span>
                </span>
                {m.badge && <span className={`mail-badge${done ? " is-done" : ""}`}>{m.badge}</span>}
                {unread && !m.badge && <span className="mail-dot" aria-hidden="true" />}
              </button>
            );
          })}
        </div>

        <div className="mail-read">
          {selected ? (
            <article className="mail-msg">
              <h2 className="mail-msg-subject">{selected.subject}</h2>
              <div className="mail-msg-meta">
                <span className="mail-avatar lg">{initialsOf(selected)}</span>
                <span className="mail-msg-meta-text">
                  <span className="mail-msg-from">
                    {selected.from}
                    {selected.fromAddress && (
                      <span className="mail-msg-addr"> &lt;{selected.fromAddress}&gt;</span>
                    )}
                  </span>
                  <span className="mail-msg-to">
                    To: {selected.to ?? "you"} {selected.receivedAt ? `\u00B7 ${selected.receivedAt}` : ""}
                  </span>
                </span>
              </div>
              <div className="mail-msg-body">
                {selected.body.split(/\n{2,}/).map((para, i) => (
                  <p key={i}>
                    {para.split(/\n/).map((ln, j) => (
                      <span key={j}>
                        {ln}
                        {j < para.split(/\n/).length - 1 ? <br /> : null}
                      </span>
                    ))}
                  </p>
                ))}
                {selected.cta && (
                  <div className="mail-cta-wrap">
                    <button
                      type="button"
                      className="mail-cta blink"
                      onClick={() => onClickLink?.(selected.id)}
                      onMouseEnter={() => setHoverHref(selected.cta!.url)}
                      onMouseLeave={() => setHoverHref(null)}
                      onFocus={() => setHoverHref(selected.cta!.url)}
                      onBlur={() => setHoverHref(null)}
                    >
                      {selected.cta.label}
                    </button>
                    <a
                      className="mail-cta-url"
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        onClickLink?.(selected.id);
                      }}
                      onMouseEnter={() => setHoverHref(selected.cta!.url)}
                      onMouseLeave={() => setHoverHref(null)}
                    >
                      {selected.cta.displayUrl ?? selected.cta.url}
                    </a>
                  </div>
                )}
                {selected.attachments && selected.attachments.length > 0 && (
                  <div className="mail-attachments">
                    <div className="mail-attach-head">
                      {"\u{1F4CE}"} {selected.attachments.length} attachment
                      {selected.attachments.length > 1 ? "s" : ""}
                    </div>
                    {selected.attachments.map((att) => (
                      <div
                        key={att.id}
                        className={`mail-attach ${att.danger ? "danger" : ""} ${
                          saveAttachmentEmailId === selected.id && att.danger ? "blink" : ""
                        }`}
                      >
                        <span className="mail-attach-ico">{ATT_ICON[att.type ?? "doc"] ?? "\u{1F4C4}"}</span>
                        <span className="mail-attach-meta">
                          <span className="mail-attach-name">{att.name}</span>
                          {att.size && <span className="mail-attach-size">{att.size}</span>}
                        </span>
                        <button
                          type="button"
                          className="mail-attach-save"
                          onClick={() => onSaveAttachment?.(selected.id, att.id)}
                        >
                          {"\u2B07"} Save
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {replyEmailId === selected.id && (
                <div className="mail-reply">
                  {replied === selected.id ? (
                    <div className="mail-reply-sent">
                      <div className="mail-reply-sent-head">
                        <span className="mail-reply-tick" aria-hidden>{"\u2713"}</span> Reply sent
                      </div>
                      {selected.replyDraft && <div className="mail-reply-quote">{selected.replyDraft}</div>}
                    </div>
                  ) : (
                    <div className="mail-reply-compose">
                      <div className="mail-reply-head">Reply to {selected.from}</div>
                      <div className="mail-reply-draft">
                        {selected.replyDraft ?? "Yes, I'd love to take part — thank you so much!"}
                      </div>
                      <button
                        type="button"
                        className="btn mail-reply-send blink"
                        onClick={() => {
                          setReplied(selected.id);
                          onReply?.(selected.id);
                        }}
                      >
                        {"\u27A4"} Send reply
                      </button>
                    </div>
                  )}
                </div>
              )}
              {continueEmailId === selected.id && onContinue && (
                <div className="mail-continue-wrap">
                  <button
                    type="button"
                    className="btn mail-continue"
                    onClick={() => onContinue(selected.id)}
                  >
                    Continue
                  </button>
                </div>
              )}
            </article>
          ) : (
            <div className="mail-empty">
              <div className="mail-empty-art">{"\u2709"}</div>
              <div className="mail-empty-title">
                {config.emptyTitle ?? "No conversation selected"}
              </div>
              <div className="mail-empty-sub">
                {config.emptyHint ?? "Select an email to read it."}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* browser-style status bar: reveals the REAL link target on hover */}
      <div className={`mail-statusbar ${hoverHref ? "show" : ""}`}>
        {hoverHref ?? ""}
      </div>
    </div>
  );
}
